import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Clock, FileDown, Mail, Trash2, RefreshCw, X, Users } from 'lucide-react';
import { corporateCardApi, CorporateCardUsage, CorporateCardUsageItem } from '../services/corporateCardApi';
import { corporateCardUploadApi, UploadStatusResponse, CorporateCardUpload } from '../services/corporateCardUploadApi';
import { useAlert } from '../contexts/AlertContext';

const CorporateCardManagement: React.FC = () => {
  const { showToast, showConfirm } = useAlert();

  // 탭 상태
  const [mainTab, setMainTab] = useState<'status' | 'consolidated'>('status');

  // 년/월 선택 - 직원 업로드 기준에 맞춰 조회 연월 계산
  // - 1일~2일: 전월 데이터 업로드 마감 기간 → 전월 조회
  // - 3일 이후: 당월 데이터 업로드 기간 → 당월 조회
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();

  const getUploadPeriod = () => {
    if (currentDay <= 2) {
      // 1일~2일: 전월 데이터 업로드 마감 기간
      let month = currentMonth - 1;
      let year = currentYear;
      if (month === 0) {
        month = 12;
        year = currentYear - 1;
      }
      return { year, month };
    } else {
      // 3일 이후: 당월 데이터 업로드 기간
      return { year: currentYear, month: currentMonth };
    }
  };

  const uploadPeriod = getUploadPeriod();
  const [selectedYear, setSelectedYear] = useState(uploadPeriod.year);
  const [selectedMonth, setSelectedMonth] = useState(uploadPeriod.month);

  // 업로드 현황
  const [uploadStatus, setUploadStatus] = useState<UploadStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedEmployeeUpload, setSelectedEmployeeUpload] = useState<CorporateCardUpload | null>(null);

  // 취합 데이터 (기존 로직)
  const [usages, setUsages] = useState<CorporateCardUsage[]>([]);
  const [selectedUsage, setSelectedUsage] = useState<CorporateCardUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // 업로드 모달 상태
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadYear, setUploadYear] = useState(now.getFullYear());
  const [uploadMonth, setUploadMonth] = useState(now.getMonth() + 1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // 수정 모달 상태
  const [editingItem, setEditingItem] = useState<CorporateCardUsageItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // 상세 탭 상태
  const [detailTab, setDetailTab] = useState<'summary' | 'detail'>('summary');

  useEffect(() => {
    fetchUsages();
  }, []);

  useEffect(() => {
    if (mainTab === 'status') {
      fetchUploadStatus();
    }
  }, [mainTab, selectedYear, selectedMonth]);

  const fetchUploadStatus = async () => {
    try {
      setStatusLoading(true);
      setSelectedEmployeeUpload(null);
      const data = await corporateCardUploadApi.getUploadStatus(selectedYear, selectedMonth);
      setUploadStatus(data);
    } catch (error: any) {
      showToast('업로드 현황을 불러오는데 실패했습니다.', 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSelectEmployee = (employeeId: number) => {
    if (!uploadStatus) return;
    const upload = uploadStatus.uploads.find(u => u.employeeId === employeeId);
    if (upload) {
      setSelectedEmployeeUpload(upload);
    }
  };

  const fetchUsages = async () => {
    try {
      setLoading(true);
      const data = await corporateCardApi.getAll();
      setUsages(data);
    } catch (error) {
      showToast('법인카드 사용내역 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConsolidate = async () => {
    if (!uploadStatus || (uploadStatus.confirmed === 0 && uploadStatus.draft === 0)) {
      showToast('업로드된 내역이 없습니다.', 'error');
      return;
    }

    let confirmMessage = `${selectedYear}년 ${selectedMonth}월 법인카드 사용내역을 취합하시겠습니까?`;
    if (uploadStatus.draft > 0) {
      confirmMessage += `\n\n• 확정: ${uploadStatus.confirmed}명\n• 임시저장: ${uploadStatus.draft}명 (자동 확정됨)`;
    } else {
      confirmMessage += `\n\n• 확정: ${uploadStatus.confirmed}명`;
    }

    const confirmed = await showConfirm(confirmMessage);
    if (!confirmed) return;

    try {
      setStatusLoading(true);
      const result = await corporateCardUploadApi.consolidate(selectedYear, selectedMonth);
      showToast(result.message, 'success');
      if (result.warning) {
        showToast(result.warning, 'info');
      }

      // 취합 데이터 새로고침
      const updatedUsages = await corporateCardApi.getAll();
      setUsages(updatedUsages);

      // 업로드 현황도 갱신 (DRAFT → CONFIRMED 반영)
      await fetchUploadStatus();

      setMainTab('consolidated');

      // 방금 취합한 내역 자동 선택
      const newUsage = updatedUsages.find(u => u.year === selectedYear && u.month === selectedMonth);
      if (newUsage) {
        const detailed = await corporateCardApi.getById(newUsage.id);
        setSelectedUsage(detailed);
      }
    } catch (error: any) {
      showToast('취합 실패: ' + error.message, 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSelectUsage = async (usage: CorporateCardUsage) => {
    try {
      const detailed = await corporateCardApi.getById(usage.id);
      setSelectedUsage(detailed);
    } catch (error) {
      showToast('상세 정보를 불러오는데 실패했습니다.', 'error');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast('파일을 선택해주세요.', 'error');
      return;
    }

    try {
      setUploading(true);
      const result = await corporateCardApi.upload(selectedFile, uploadYear, uploadMonth);
      showToast(result.message, 'success');
      setIsUploadModalOpen(false);
      setSelectedFile(null);

      const data = await corporateCardApi.getAll();
      setUsages(data);

      // 전체(자동분리) 모드에서는 첫 번째 결과 월을 선택, 단일 월이면 해당 월 선택
      if (uploadMonth === 0 && result.results) {
        const firstResult = result.results[0];
        if (firstResult) {
          const newUsage = data.find(u => u.year === firstResult.year && u.month === firstResult.month);
          if (newUsage) {
            const detailed = await corporateCardApi.getById(newUsage.id);
            setSelectedUsage(detailed);
          }
        }
      } else {
        const newUsage = data.find(u => u.year === uploadYear && u.month === uploadMonth);
        if (newUsage) {
          const detailed = await corporateCardApi.getById(newUsage.id);
          setSelectedUsage(detailed);
        }
      }
    } catch (error: any) {
      showToast('업로드 실패: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateTransferFile = async (id: number) => {
    const confirmed = await showConfirm('이체파일을 생성하고 다운로드하시겠습니까?');
    if (!confirmed) return;

    try {
      const result = await corporateCardApi.generateTransfer(id);

      if (result.success && selectedUsage) {
        const downloadUrl = corporateCardApi.getTransferDownloadUrl(id);
        const token = localStorage.getItem('accessToken');

        const response = await fetch(downloadUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const today = new Date();
          const downloadDate = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
          a.download = `${selectedUsage.year}_${String(selectedUsage.month).padStart(2, '0')}_법인카드정산_${downloadDate}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      }

      const detailed = await corporateCardApi.getById(id);
      setSelectedUsage(detailed);
    } catch (error: any) {
      showToast('이체파일 생성 실패: ' + error.message, 'error');
    }
  };

  const handleDownloadExcel = async (id: number) => {
    try {
      const downloadUrl = corporateCardApi.getExcelDownloadUrl(id);
      const token = localStorage.getItem('accessToken');

      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const today = new Date();
        const downloadDate = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
        a.download = `${selectedUsage?.year}_${String(selectedUsage?.month).padStart(2, '0')}_법인카드사용내역_${downloadDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error: any) {
      showToast('엑셀 다운로드 실패: ' + error.message, 'error');
    }
  };

  const handleEditItem = (item: CorporateCardUsageItem) => {
    setEditingItem({ ...item });
    setIsEditModalOpen(true);
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;
    try {
      await corporateCardApi.updateItem(editingItem.id, editingItem);
      showToast('수정 완료', 'success');
      setIsEditModalOpen(false);
      if (selectedUsage) {
        const updated = await corporateCardApi.getById(selectedUsage.id);
        setSelectedUsage(updated);
      }
    } catch (error: any) {
      showToast('수정 실패: ' + error.message, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await showConfirm('정말 삭제하시겠습니까? 관련된 모든 데이터가 삭제됩니다.');
    if (!confirmed) return;
    try {
      await corporateCardApi.delete(id);
      showToast('삭제 완료', 'success');
      setSelectedUsage(null);
      fetchUsages();
    } catch (error: any) {
      showToast('삭제 실패: ' + error.message, 'error');
    }
  };

  const handleSendEmail = async (id: number) => {
    const confirmed = await showConfirm('ceo@grkcon.com\nadmin@grkcon.com\n\n으로 이체파일을 전송하시겠습니까?');
    if (!confirmed) return;
    try {
      const result = await corporateCardApi.sendTransferEmail(id);
      showToast(result.message, result.success ? 'success' : 'error');
    } catch (error: any) {
      showToast('이메일 전송 실패: ' + error.message, 'error');
    }
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('ko-KR').format(Math.round(num));
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">확정</span>;
      case 'DRAFT':
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">임시저장</span>;
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">법인카드 정산</h1>
      </div>

      {/* 메인 탭 */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setMainTab('status')}
              className={`px-6 py-4 text-sm font-medium ${
                mainTab === 'status'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users size={18} className="inline mr-2" />
              직원 업로드 현황
            </button>
            <button
              onClick={() => setMainTab('consolidated')}
              className={`px-6 py-4 text-sm font-medium ${
                mainTab === 'consolidated'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileDown size={18} className="inline mr-2" />
              취합 및 정산
            </button>
          </nav>
        </div>

        {/* 업로드 현황 탭 */}
        {mainTab === 'status' && (
          <div className="p-6">
            {/* 년/월 선택 */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {[currentYear - 2, currentYear - 1, currentYear].map((y) => (
                    <option key={y} value={y}>{y}년</option>
                  ))}
                </select>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>{m}월</option>
                  ))}
                </select>
              </div>
              <button
                onClick={fetchUploadStatus}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw size={18} />
              </button>
              <button
                onClick={handleConsolidate}
                disabled={!uploadStatus || (uploadStatus.confirmed === 0 && uploadStatus.draft === 0)}
                className={`px-4 py-2 rounded-lg text-white ${
                  uploadStatus && (uploadStatus.confirmed > 0 || uploadStatus.draft > 0)
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                취합하기
              </button>
            </div>

            {statusLoading ? (
              <div className="text-center py-12 text-gray-500">로딩 중...</div>
            ) : uploadStatus ? (
              <>
                {/* 요약 카드 */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                      <CheckCircle size={16} />
                      확정 완료
                    </div>
                    <div className="text-2xl font-bold text-green-600">{uploadStatus.confirmed}명</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-yellow-600 text-sm mb-1">
                      <Clock size={16} />
                      임시저장
                    </div>
                    <div className="text-2xl font-bold text-yellow-600">{uploadStatus.draft}명</div>
                  </div>
                </div>

                {/* 직원별 현황 */}
                {uploadStatus.employees.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    {selectedYear}년 {selectedMonth}월에 업로드한 직원이 없습니다.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* 직원 목록 */}
                    <div className="lg:col-span-1">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">제출 직원 목록</h3>
                      <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                        {uploadStatus.employees.map((emp) => (
                          <div
                            key={emp.id}
                            onClick={() => handleSelectEmployee(emp.id)}
                            className={`px-4 py-3 cursor-pointer transition-colors ${
                              selectedEmployeeUpload?.employeeId === emp.id
                                ? 'bg-blue-50 border-l-4 border-l-blue-500'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium">{emp.name}</span>
                              {getStatusBadge(emp.status)}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              {emp.amount > 0 ? `${formatCurrency(emp.amount)}원` : '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 직원 상세 내역 */}
                    <div className="lg:col-span-2">
                      {selectedEmployeeUpload ? (
                        <div className="border rounded-lg">
                          <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
                            <div>
                              <h3 className="font-medium">
                                {selectedEmployeeUpload.employee?.name || '직원'} 사용내역
                              </h3>
                              <div className="text-sm text-gray-500 mt-1">
                                {selectedEmployeeUpload.itemCount}건 · 총 {formatCurrency(Number(selectedEmployeeUpload.totalAmount))}원
                              </div>
                            </div>
                            <button
                              onClick={() => setSelectedEmployeeUpload(null)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X size={20} />
                            </button>
                          </div>
                          <div className="overflow-x-auto max-h-[350px] overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-100 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left">일자</th>
                                  <th className="px-3 py-2 text-left">사용처</th>
                                  <th className="px-3 py-2 text-right">금액</th>
                                  <th className="px-3 py-2 text-left">적요</th>
                                  <th className="px-3 py-2 text-left">비고</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedEmployeeUpload.items.map((item) => (
                                  <tr key={item.id} className="border-b hover:bg-gray-50">
                                    <td className="px-3 py-2">{formatDate(item.usageDate)}</td>
                                    <td className="px-3 py-2">{item.merchant}</td>
                                    <td className="px-3 py-2 text-right font-medium">
                                      {formatCurrency(Number(item.amount))}원
                                    </td>
                                    <td className="px-3 py-2 text-gray-500">{item.description || '-'}</td>
                                    <td className="px-3 py-2 text-gray-500">{item.memo || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50 sticky bottom-0">
                                <tr>
                                  <td colSpan={2} className="px-3 py-2 font-medium">합계</td>
                                  <td className="px-3 py-2 text-right font-bold text-blue-600">
                                    {formatCurrency(Number(selectedEmployeeUpload.totalAmount))}원
                                  </td>
                                  <td colSpan={2}></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="border rounded-lg p-8 text-center text-gray-500">
                          좌측에서 직원을 선택하면 상세 내역을 확인할 수 있습니다.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                년/월을 선택하면 업로드 현황을 확인할 수 있습니다.
              </div>
            )}
          </div>
        )}

        {/* 취합 및 정산 탭 */}
        {mainTab === 'consolidated' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* 월별 목록 */}
              <div className="lg:col-span-1">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold">취합 목록</h2>
                  <button
                    onClick={() => setIsUploadModalOpen(true)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    직접 업로드
                  </button>
                </div>
                <div className="space-y-2">
                  {loading ? (
                    <div className="text-gray-500 text-center py-8">로딩 중...</div>
                  ) : usages.length === 0 ? (
                    <div className="text-gray-500 text-center py-8">데이터가 없습니다.</div>
                  ) : (
                    usages.map((usage) => (
                      <div
                        key={usage.id}
                        onClick={() => handleSelectUsage(usage)}
                        className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                          selectedUsage?.id === usage.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{usage.year}년 {usage.month}월</span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {usage.employeeCount}명 / {formatCurrency(usage.totalAmount)}원
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* 상세 정보 */}
              <div className="lg:col-span-3">
                {selectedUsage ? (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-lg font-semibold">
                        {selectedUsage.year}년 {selectedUsage.month}월 법인카드 사용내역
                      </h2>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleGenerateTransferFile(selectedUsage.id)}
                          className="flex items-center gap-1 px-3 py-1 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700"
                        >
                          <FileDown size={16} />
                          이체파일
                        </button>
                        <button
                          onClick={() => handleSendEmail(selectedUsage.id)}
                          className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                        >
                          <Mail size={16} />
                          메일전송
                        </button>
                        <button
                          onClick={() => handleDownloadExcel(selectedUsage.id)}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                        >
                          엑셀 다운
                        </button>
                        <button
                          onClick={() => handleDelete(selectedUsage.id)}
                          className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                        >
                          <Trash2 size={16} />
                          삭제
                        </button>
                      </div>
                    </div>

                    {/* 요약 정보 */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500">대상 인원</div>
                        <div className="text-xl font-bold">{selectedUsage.employeeCount}명</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500">총 사용금액</div>
                        <div className="text-xl font-bold text-blue-600">
                          {formatCurrency(selectedUsage.totalAmount)}원
                        </div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500">사용건수</div>
                        <div className="text-xl font-bold">{selectedUsage.items?.length || 0}건</div>
                      </div>
                    </div>

                    {/* 상세 탭 */}
                    <div className="border-b border-gray-200 mb-4">
                      <nav className="flex gap-4">
                        <button
                          onClick={() => setDetailTab('summary')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            detailTab === 'summary'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          직원별 합계
                        </button>
                        <button
                          onClick={() => setDetailTab('detail')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            detailTab === 'detail'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          전체 내역
                        </button>
                      </nav>
                    </div>

                    {/* 직원별 합계 */}
                    {detailTab === 'summary' && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left">사용자</th>
                              <th className="px-4 py-2 text-right">합계금액</th>
                              <th className="px-4 py-2 text-left">은행</th>
                              <th className="px-4 py-2 text-left">계좌번호</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedUsage.employeeSummary || []).map((summary, idx) => (
                              <tr key={idx} className="border-b hover:bg-gray-50">
                                <td className="px-4 py-2">{summary.name}</td>
                                <td className="px-4 py-2 text-right font-semibold text-blue-600">
                                  {formatCurrency(summary.total)}원
                                </td>
                                <td className="px-4 py-2">{summary.employee?.bankName || '-'}</td>
                                <td className="px-4 py-2">{summary.employee?.bankAccount || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50">
                            <tr>
                              <td className="px-4 py-2 font-semibold">총합계</td>
                              <td className="px-4 py-2 text-right font-bold text-blue-600">
                                {formatCurrency(selectedUsage.totalAmount)}원
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}

                    {/* 전체 내역 */}
                    {detailTab === 'detail' && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-2 py-2 text-left whitespace-nowrap">사용자</th>
                              <th className="px-2 py-2 text-left whitespace-nowrap">일자</th>
                              <th className="px-2 py-2 text-left">사용처</th>
                              <th className="px-2 py-2 text-right whitespace-nowrap">금액</th>
                              <th className="px-2 py-2 text-left">적요</th>
                              <th className="px-2 py-2 text-left">비고</th>
                              <th className="px-2 py-2 text-center whitespace-nowrap">수정</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedUsage.items || []).map((item) => (
                              <tr key={item.id} className="border-b hover:bg-gray-50">
                                <td className="px-2 py-2 whitespace-nowrap">{item.employeeName}</td>
                                <td className="px-2 py-2 whitespace-nowrap">{formatDate(item.usageDate)}</td>
                                <td className="px-2 py-2">{item.merchant}</td>
                                <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">
                                  {formatCurrency(item.amount)}원
                                </td>
                                <td className="px-2 py-2 text-gray-600">{item.description || '-'}</td>
                                <td className="px-2 py-2 text-gray-600">{item.memo || '-'}</td>
                                <td className="px-2 py-2 text-center whitespace-nowrap">
                                  <button
                                    onClick={() => handleEditItem(item)}
                                    className="text-blue-600 hover:underline"
                                  >
                                    수정
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-gray-500 text-center py-16">
                    왼쪽에서 월별 내역을 선택하세요.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 업로드 모달 */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">법인카드 사용내역 업로드</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">년도</label>
                  <select
                    value={uploadYear}
                    onChange={(e) => setUploadYear(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {[currentYear - 2, currentYear - 1, currentYear].map((year) => (
                      <option key={year} value={year}>{year}년</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">월</label>
                  <select
                    value={uploadMonth}
                    onChange={(e) => setUploadMonth(Number(e.target.value))}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value={0}>전체 (자동분리)</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <option key={month} value={month}>{month}월</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">엑셀 파일</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setSelectedFile(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? '업로드 중...' : '업로드'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {isEditModalOpen && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px]">
            <h3 className="text-lg font-semibold mb-4">사용내역 수정</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">사용자</label>
                <input
                  type="text"
                  value={editingItem.employeeName}
                  onChange={(e) => setEditingItem({ ...editingItem, employeeName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">일자</label>
                <input
                  type="date"
                  value={editingItem.usageDate.split('T')[0]}
                  onChange={(e) => setEditingItem({ ...editingItem, usageDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">사용처</label>
                <input
                  type="text"
                  value={editingItem.merchant}
                  onChange={(e) => setEditingItem({ ...editingItem, merchant: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">금액</label>
                <input
                  type="number"
                  value={editingItem.amount}
                  onChange={(e) => setEditingItem({ ...editingItem, amount: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">적요</label>
                <input
                  type="text"
                  value={editingItem.description || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">비고</label>
                <input
                  type="text"
                  value={editingItem.memo || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, memo: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleSaveItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateCardManagement;
